import { useEffect, useState, type FormEvent } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  confirmWithPassword, enrollTotp, firstVerifiedTotpFactor, verifyTotp,
  type StepUpMethod, type TotpEnrollment,
} from '../lib/auth';

/**
 * Owner re-verification before protected actions: the authenticator code (aal2), or - when the
 * owner chose it in Settings - the account password, which then counts for 15 minutes.
 */
export function StepUp({ client, method = 'authenticator', email = null, onVerified, onCancel }: {
  client: SupabaseClient;
  method?: StepUpMethod;
  email?: string | null;
  onVerified: () => void;
  onCancel: () => void;
}) {
  const [usePassword, setUsePassword] = useState(method === 'password' && email !== null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(!usePassword);

  useEffect(() => {
    if (usePassword) return undefined;
    let active = true;
    setLoading(true);
    void (async () => {
      const existing = await firstVerifiedTotpFactor(client);
      if (!active) return;
      if (existing) setFactorId(existing);
      else {
        const created = await enrollTotp(client);
        if (!active) return;
        if (created) { setEnrollment(created); setFactorId(created.factorId); }
        else setMessage('Could not start authenticator setup.');
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [client, usePassword]);

  const onSubmitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!factorId) return;
    const problem = await verifyTotp(client, factorId, code.trim());
    if (problem) setMessage(problem);
    else onVerified();
  };

  const onSubmitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (email === null) return;
    const problem = await confirmWithPassword(client, email, password);
    setPassword('');
    if (problem) setMessage(problem);
    else onVerified();
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Owner verification">
      <div className="card">
        <h2>Owner verification</h2>
        {usePassword ? (
          <form onSubmit={onSubmitPassword} className="stack">
            <p className="muted">Enter your password. It covers protected actions for the next 15 minutes.</p>
            <label>Password<input type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} required autoFocus /></label>
            <button type="submit" className="primary">Confirm</button>
          </form>
        ) : (
          <>
            {loading && <p>Preparing…</p>}
            {enrollment && (
              <>
                <p>Scan this code with your authenticator app, then enter the 6-digit code.</p>
                <img src={enrollment.qrCode} alt="Authenticator setup QR code" width={180} height={180} />
              </>
            )}
            {!loading && factorId && (
              <form onSubmit={onSubmitCode} className="stack">
                <label>Code<input inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} required /></label>
                <button type="submit" className="primary">Verify</button>
              </form>
            )}
          </>
        )}
        {message && <p role="alert">{message}</p>}
        {method === 'password' && email !== null && (
          <button type="button" className="link" onClick={() => { setMessage(null); setUsePassword((value) => !value); }}>
            {usePassword ? 'Use the authenticator code instead' : 'Use my password instead'}
          </button>
        )}
        <button type="button" className="link" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
